/**
 * Base service class providing common API operations
 * All feature-specific services should extend this class
 * Routes through FastAPI proxy for authentication
 */
import { AxiosResponse } from 'axios';
import { proxyService } from './proxy.service';
import { ApiResponse, PaginatedResponse, QueryParams } from '@core/types';

/**
 * Generic base service for API operations
 * @template T - The entity type this service handles
 */
export class BaseService<T> {
    protected endpoint: string;

    /**
     * Creates a new base service instance
     * @param endpoint - The API endpoint path for this service
     */
    constructor(endpoint: string) {
        this.endpoint = endpoint;
    }

    /**
     * Fetches all items with optional pagination and filtering
     * @param params - Query parameters for pagination and filtering
     * @returns Promise with paginated response or array (handles both Drupal and standard API formats)
     */
    async getAll(params?: QueryParams): Promise<PaginatedResponse<T> | T[]> {
        const response: AxiosResponse<ApiResponse<PaginatedResponse<T>> | ApiResponse<T[]> | T[]> =
            await proxyService.get(this.endpoint, { params });
        
        // Handle Drupal format (direct array)
        if (Array.isArray(response.data)) {
            return response.data;
        }
        
        // Handle standard API format (wrapped in ApiResponse)
        if (response.data && typeof response.data === 'object' && 'data' in response.data) {
            const apiResponse = response.data as ApiResponse<PaginatedResponse<T> | T[]>;
            // If data is an array, return it directly
            if (Array.isArray(apiResponse.data)) {
                return apiResponse.data;
            }
            // If data is PaginatedResponse, return it
            return apiResponse.data;
        }
        
        // Fallback: return empty array
        return [];
    }

    /**
     * Fetches a single item by ID
     * @param id - The unique identifier
     * @returns Promise with the item
     */
    async getById(id: string | number): Promise<T> {
        const response: AxiosResponse<ApiResponse<T> | T> =
            await proxyService.get(`${this.endpoint}/${id}`);
        
        // Handle Drupal format (direct object)
        if (response.data && typeof response.data === 'object' && !('data' in response.data)) {
            return response.data as T;
        }
        
        // Handle standard API format (wrapped in ApiResponse)
        const apiResponse = response.data as ApiResponse<T>;
        return apiResponse.data;
    }

    /**
     * Creates a new item
     * @param data - The item data to create
     * @returns Promise with the created item
     */
    async create(data: Partial<T>): Promise<T> {
        const response: AxiosResponse<ApiResponse<T> | T> =
            await proxyService.post(this.endpoint, data);
        
        // Handle Drupal format (direct object)
        if (response.data && typeof response.data === 'object' && !('data' in response.data)) {
            return response.data as T;
        }
        
        // Handle standard API format (wrapped in ApiResponse)
        const apiResponse = response.data as ApiResponse<T>;
        return apiResponse.data;
    }

    /**
     * Updates an existing item
     * @param id - The unique identifier
     * @param data - The updated item data
     * @returns Promise with the updated item
     */
    async update(id: string | number, data: Partial<T>): Promise<T> {
        const response: AxiosResponse<ApiResponse<T> | T> =
            await proxyService.put(`${this.endpoint}/${id}`, data);
        
        // Handle Drupal format (direct object)
        if (response.data && typeof response.data === 'object' && !('data' in response.data)) {
            return response.data as T;
        }
        
        // Handle standard API format (wrapped in ApiResponse)
        const apiResponse = response.data as ApiResponse<T>;
        return apiResponse.data;
    }

    /**
     * Deletes an item by ID
     * @param id - The unique identifier
     * @returns Promise with void
     */
    async delete(id: string | number): Promise<void> {
        await proxyService.delete(`${this.endpoint}/${id}`);
    }
}
